/**
 * ProducerPopup Presentational Component
 * For more informations about this kind of component, read README
 *
 * References:
 *  - https://usehooks.com/useWindowSize/
 */

import React, { useState, useEffect } from 'react';
import TextTransition, { presets } from 'react-text-transition';

import styles from './UserPopup.module.css';

/**
 * useProducersPopup UI logic hook.
 * Enable eventsHandlres to control UI behaviour and localStates to represent UI states.
 * @param {Commands} commands
 * @returns local states and events handles
 */
function useUserPopup(commands) {
  const producerSynonyms = ['produtores', 'empreendedores', 'artesãos', 'vendedores'];
  const consumerSynonyms = ['consumidores', 'moradores', 'visitantes', 'compradores'];

  const [step, setStep] = useState('presentation');
  const [transitionsIndex, setTransitionsIndex] = useState(-1);
  const [producerSynonym, setProducerSynonym] = useState(producerSynonyms[0]);
  const [consumerSynonym, setConsumerSynonym] = useState(consumerSynonyms[0]);

  const handlePrimaryClick = (steps, nearestAddress) => {
    setStep(steps[step].stepRight);

    switch (step) {
    case 'consumersCall':
      if (nearestAddress) commands.moveToLocation([nearestAddress.lat, nearestAddress.lng]);
      break;

    case 'producersCall':
      window.location.href = 'https://play.google.com/support/store/apps/details?id=com.locall.app&hl=pt_BR&gl=US';
      break;
    default:
      break;
    }
  };
  const handleSecondaryClick = (steps) => {
    setStep(steps[step].stepLeft);
  };

  useEffect(() => {
    const intervalId = setInterval(() => setTransitionsIndex((e) => e + 1), 3000);
    return () => clearTimeout(intervalId);
  }, []);

  useEffect(() => {
    if (transitionsIndex <= 0) return;

    setProducerSynonym(producerSynonyms[transitionsIndex % producerSynonyms.length]);
    setConsumerSynonym(consumerSynonyms[transitionsIndex % consumerSynonyms.length]);
  }, [transitionsIndex]);

  return {
    localStates: {
      step,
      transitionsIndex,
      producerSynonym,
      consumerSynonym,
    },
    eventsHandlers: {
      handlePrimaryClick,
      handleSecondaryClick,
    },
  };
}

/**
 * ProducerPopup purely presentational component
 * Get user events and pass it to UI hook
 * @returns JSX presentational component
 */
export default function UserPopup(props) {
  const {
    commands,
    sharedStates,
  } = props;

  const { localStates, eventsHandlers } = useUserPopup(commands);

  const steps = {
    presentation: {
      stepRight: 'consumersCall',
      stepLeft: 'producersCall',
      title: (
        <h3>
          Boas-vindas
          <br />
          à comunidade
          <br />
          da sua região!
        </h3>
      ),
      message: (
        <div>
          <p className={styles['justify-last-line']}>
            Um ambiente formado por nós,
          </p>
          <div className={styles['justify-last-line']}>
            <TextTransition
              text={localStates.producerSynonym}
              springConfig={presets.wobbly}
              inline
              direction="up"
              className={styles['underline-green']}
            />
            {' e '}
            <TextTransition
              text={localStates.consumerSynonym}
              springConfig={presets.wobbly}
              inline
              direction="down"
              className={styles['underline-red']}
            />
            , unidos
          </div>
          <p>
            no propósito de fortalecer a economia local e as produções de pequena escala urbanas e rurais, coletivas e
            individuais, artesanais e familiares.
          </p>
        </div>
      ),
      buttons: {
        primary: 'Conhecer região',
        secondary: 'Também sou produtor',
      },
    },

    consumersCall: {
      stepRight: 'consumersCall',
      stepLeft: 'presentation',
      title: (
        <h3>
          Explorar
          <br />
          para descobrir
        </h3>
      ),
      message: (
        <div>
          {/* Esse texto pode estar condicionado ao número de produtores da região */}
          <div>
            <p>
              Para priorizar quem está próximo de você, basta abrir a lista
              {' '}
              <span className={styles['underline-yellow']}>Espaços por perto</span>
              . Por lá ou pelo mapa, dá pra acessar o perfil de cada espaço para conhecê-lo melhor e realizar pedidos.
            </p>
          </div>
        </div>
      ),
      buttons: {
        primary: 'Ir para espaço mais próximo',
        secondary: 'Voltar',
      },
    },

    producersCall: {
      stepRight: 'producersCall',
      stepLeft: 'presentation',
      title: (
        <h3>
          As portas estão
          <br />
          sempre abertas
        </h3>
      ),
      message: (
        <div>
          {/* Esse texto pode estar condicionado ao número de produtores da região */}
          <p>
            Quer fazer parte da comunidade na sua região? 
            Então baixe o aplicativo da Locall. 
            Lá você poderá cadastrar suas ofertas, informações de contato, de entrega e outras para formar seu perfil! 
            Assim as pessoas da região poderão te conhecer melhor!
          </p>
        </div>
      ),
      buttons: {
        primary: 'Baixar o app',
        secondary: 'Voltar',
      },
    },
  };

  return (
    <div className={styles['popup-container']}>
      <div className={styles['popup-content']}>
        <section className={styles['popup-header']}>
          <div>
            {steps[localStates.step].title}
          </div>
        </section>

        <section className={styles.message}>
          {steps[localStates.step].message}
        </section>

        <section className={styles['process-options']}>
          {/* <div>
            ....
          </div> */}

          <div className={styles['process-buttons']}>
            <button
              className={styles['secondary-button']}
              type="button"
              onClick={() => eventsHandlers.handleSecondaryClick(steps)}
            >
              {steps[localStates.step].buttons.secondary}
            </button>

            {/* <div className={styles['buttons-division']} /> */}

            <button
              className={styles['primary-button']}
              type="button"
              onClick={() => eventsHandlers.handlePrimaryClick(steps, sharedStates.addressesSortedByDistance?.filter((address) => address.producer.imagesPaths?.length)[0])}
            >
              {steps[localStates.step].buttons.primary}
            </button>
          </div>
        </section>

        <section className={styles['popup-options']}>
          <div className={styles['buttons-right']} />

          <div className={styles['buttons-left']} />

        </section>
      </div>
    </div>
  ); // : <></>;
}
